<template>
  <a-modal title="Send Notification" :visible="visible" data-testid="notification-send-modal" @cancel="closeModal">
    <a-form-model
      ref="form"
      layout="horizontal"
      class="flex gap-2"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
      data-testid="notification-send-form"
    >
      <a-form-model-item :colon="false" label-align="left" label="Associate type">
        <a-radio-group v-model="associateFilterType" button-style="solid" data-testid="notification-send-associate-type-radio">
          <a-radio-button value="all">All</a-radio-button>
          <a-radio-button value="specific">Specific</a-radio-button>
        </a-radio-group>
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Title" prop="title">
        <a-input v-model="form.title" placeholder="Enter title" data-testid="notification-send-title-input" />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Message" prop="message" class="message-field">
        <a-textarea
          v-model="form.message"
          placeholder="Enter your message"
          rows="4"
          data-testid="notification-send-message-input"
        />
      </a-form-model-item>

      <template
        ><template v-if="associateFilterType === 'all'">
          <a-form-model-item :colon="false" label-align="left" label="Payment type" prop="payment_types">
            <custom-select
              :multiple="true"
              :value.sync="form.payment_types"
              placeholder="Payment Type"
              :options="paymentTypes"
              name="value"
              optionKey="key"
              data-testid="notification-send-payment-types-select"
            />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Position" prop="roles">
            <custom-select
              :multiple="true"
              :value.sync="form.roles"
              placeholder="Position"
              :options="projectRoles"
              name="name"
              optionKey="id"
              data-testid="notification-send-positions-select"
            />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Legal Entity" prop="roles">
            <custom-select
              :multiple="true"
              :value.sync="form.legal_entities"
              placeholder="Legal Entity"
              :options="legalEntities"
              name="name"
              optionKey="id"
              data-testid="notification-send-legal-entities-select"
            />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Department" prop="roles">
            <custom-select
              :multiple="true"
              :value.sync="form.departments"
              placeholder="Department"
              :options="departments"
              name="name"
              optionKey="id"
              data-testid="notification-send-departments-select"
            />
          </a-form-model-item>
        </template>

        <template v-else>
          <a-form-model-item :colon="false" label-align="left" label="Associates" prop="associates">
            <search-associates
              :multiply="true"
              :value.sync="form.associates"
              data-testid="notification-send-associates-select"
            /> </a-form-model-item></template
      ></template>

      <a-form-model-item :colon="false" label-align="left" label="Working Status" prop="working_only">
        <a-checkbox v-model="form.working_only" data-testid="notification-send-working-only-checkbox">
          Only working at the moment
        </a-checkbox>
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Notification Types" prop="notification_types">
        <custom-select
          :multiple="true"
          :value.sync="form.notification_types"
          placeholder="Notification Types"
          :options="alertTypes"
          name="name"
          optionKey="id"
          data-testid="notification-send-types-select"
        />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Files" prop="files">
        <attachments-field
          :with-title="false"
          :files="fileList"
          data-testid="notification-send-files-field"
          @save="saveAttachmentsIds"
        />
      </a-form-model-item>
    </a-form-model>
    <template slot="footer">
      <div class="flex justify-content-between" data-testid="notification-send-footer">
        <a-button data-testid="notification-send-cancel-button" @click="closeModal">Cancel</a-button>
        <a-button type="primary" :loading="loading" data-testid="notification-send-submit-button" @click="sendNotification">
          Send
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import SearchAssociates from '@/components/search-associates.vue'
import CustomSelect from '@/components/ui/custom-select.vue'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import { NOTIFICATION_SOURCE } from '@/store/modules/alerts/constants'
import { PAYMENT_TYPE } from '@/store/modules/payment/constants'
import { deepCopy } from '@/utils/deep-copy'
import { Modal, Button, Input, notification, Radio, FormModel, Checkbox } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions } = createNamespacedHelpers('alerts')

const initialForm = {
  notification_types: ['with_alert'],
  working_only: false,
  roles: [],
  payment_types: [],
  departments: [],
  legal_entities: [],
  associates: [],
  title: undefined,
  message: undefined
}

export default {
  components: {
    'a-modal': Modal,
    'a-input': Input,
    'a-textarea': Input.TextArea,
    'a-button': Button,
    'a-radio-group': Radio.Group,
    'a-radio-button': Radio.Button,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-checkbox': Checkbox,
    'search-associates': SearchAssociates,
    'custom-select': CustomSelect,
    'attachments-field': ShiftFormAttachments
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    associate_number: {
      type: String,
      default: undefined
    },
    notification: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      alertTypes: NOTIFICATION_SOURCE,
      paymentTypes: Object.entries(PAYMENT_TYPE).map(([key, value]) => ({
        key,
        value
      })),

      form: deepCopy(initialForm),
      rules: {
        title: [{ required: true, message: 'Title is required', trigger: 'change' }],
        message: [{ required: true, message: 'Message is required', trigger: 'change' }],
        notification_types: [
          {
            required: true,
            message: 'Notification Types is required',
            trigger: 'change'
          }
        ],
        associates: [
          {
            required: true,
            message: 'Associates is required',
            trigger: 'change'
          }
        ]
      },

      associateFilterType: 'all',

      attachmentsIds: [],
      fileList: [],

      loading: false
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal && this.associate_number) {
          this.associateFilterType = 'specific'
          this.$set(this.form, 'associates', [this.associate_number])
        }

        if (newVal && this.notification) {
          this.form.legal_entities = this.notification.legal_entities.map(item => item.id)
          this.form.roles = this.notification.roles.map(item => item.id)
          this.form.payment_types = this.notification.payment_types
          this.form.departments = this.notification.departments.map(item => item.id)
          this.form.title = this.notification.title
          this.form.message = this.notification.message
          this.form.associates = this.notification.associates.map(item => item.associate_number)
          this.form.working_only = this.notification.working_only
          this.fileList = this.notification.files
          this.attachmentsIds = this.notification.files.map(item => ({
            id: item.id
          }))

          this.form.notification_types = Object.keys(this.notification).filter(
            key => this.alertTypes.map(item => item.id).includes(key) && this.notification[key] === true
          )

          if (this.notification.associates.length) {
            this.associateFilterType = 'specific'
          }
        }
      },
      immediate: true
    }
  },
  computed: {
    projectRoles() {
      return this.$store.state.applicationState.projectRoles || []
    },
    legalEntities() {
      return this.$store.state.applicationState.projectLegalEntities
    },
    departments() {
      return this.$store.state.applicationState.projectDepartments
    }
  },
  methods: {
    ...mapActions(['postNotification']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    saveAttachmentsIds(ids) {
      this.attachmentsIds = [...ids]
    },

    async sendNotification() {
      let timeoutId
      let isRequestCompleted = false

      try {
        this.loading = true

        const valid = await new Promise(resolve => {
          this.$refs.form.validate(resolve)
        })

        if (!valid) {
          this.showNotification(
            'info',
            'Info',
            'Some fields are not filled in or are filled in incorrectly. Please check the fields'
          )

          return
        }

        const payload = {
          notification: {
            title: this.form.title,
            message: this.form.message,
            files: this.attachmentsIds,
            working_only: this.form.working_only,
            ...(this.associateFilterType === 'all'
              ? {
                  roles: this.form.roles,
                  payment_types: this.form.payment_types,
                  departments: this.form.departments,
                  legal_entities: this.form.legal_entities
                }
              : {
                  associates: this.form.associates
                }),

            ...this.alertTypes.reduce((acc, { id }) => {
              acc[id] = this.form.notification_types.includes(id)
              return acc
            }, {})
          }
        }

        timeoutId = setTimeout(() => {
          if (!isRequestCompleted) {
            notification.warning({
              message: 'Timeout',
              description:
                'The volume of messages we need to send is pretty high, so we’re still working on it. Please wait — a couple of minutes might be needed to send everything!'
            })
          }
        }, 10000)

        await this.postNotification(payload)

        notification.success({
          message: 'Successfully sent',
          description: 'Notification(s) sent successfully!'
        })

        isRequestCompleted = true

        this.$emit('edit')
        this.closeModal()
      } catch (error) {
        isRequestCompleted = true

        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.loading = false
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
      }
    },
    closeModal() {
      this.attachmentsIds = []
      this.fileList = []
      this.form = deepCopy(initialForm)
      this.$emit('update:visible', false)
      this.$emit('update:associate_number', undefined)
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}
</style>
